import React, { useEffect, useState } from "react";
import "./SportsProfilePage.css";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import api from "@api";
import {
  Trophy,
  Camera,
  Star,
  BadgeCheck,
  Play,
  Image,
  MessageCircle,
  UserRound,
} from "lucide-react";
import DirectoryNavbar from "../Components/navbar/DirectoryNavbar.jsx";
import mockSportsProfile from "./mockSportsProfile.js";
import FriendRequestModal from "./FriendRequestModal.jsx";

const SportsProfilePage = () => {
  const [activeTab, setActiveTab] = useState("timeline");
  const { user } = useAuth();
  const { id } = useParams();
  const [showFriendRequestModal, setShowFriendRequestModal] = useState(false);
  const [showFriendMenu, setShowFriendMenu] = useState(false);
  const [profile, setProfile] = useState(null);
  const [friendStatus, setFriendStatus] = useState("none");
  const [connectionId, setConnectionId] = useState(null);
  const [friends, setFriends] = useState([]);
  const [startingLineup, setStartingLineup] = useState([]);
  const [sports, setSports] = useState([]);
  const isOwnProfile = user?.id === profile?.user_id;
  const navigate = useNavigate();


  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get(`/api/user-profiles-v2/${id}`);
        setProfile(res.data.profile);
      } catch (error) {
        console.error("Failed to fetch profile:", error);
      }
    };

    fetchProfile();
  }, [id]);

  useEffect(() => {
    const fetchFriendStatus = async () => {
      if (!profile || isOwnProfile) return;

      try {
        const res = await api.get(
          `/api/friends/status/${profile.id}`,
          {
            withCredentials: true,
          }
        );
        setFriendStatus(res.data.status);
        setConnectionId(res.data.connection_id);
      } catch (error) {
        console.error("Failed to fetch friend status:", error);
      }
    };

    fetchFriendStatus();
  }, [profile, isOwnProfile]);

  useEffect(() => {
    const fetchStartingLineup = async () => {
      if (!profile) return;

      try {
        const res = await api.get(
          `/api/starting-lineup/${profile.id}`
        );
        console.log("STARTING LINEUP RESPONSE:", res.data);
        setStartingLineup(res.data.lineup || []);
      } catch (error) {
        console.error("Failed to fetch Starting Lineup:", error);
      }
    };

    fetchStartingLineup();
  }, [profile]);

  useEffect(() => {
    const refreshFriendStatus = async () => {
      if (!profile || isOwnProfile) return;

      const res = await api.get(`/api/friends/status/${profile.id}`);

      setFriendStatus(res.data.status);
      setConnectionId(res.data.connection_id);
    };

    window.addEventListener("friend-status-changed", refreshFriendStatus);

    return () => {
      window.removeEventListener("friend-status-changed", refreshFriendStatus);
    };
  }, [profile, isOwnProfile]);


  useEffect(() => {
    const fetchFriends = async () => {
      if (!profile) return;

      try {
        const res = await api.get(`/api/friends/${profile.id}`);

        setFriends(res.data.friends || res.data || []);
      } catch (error) {
        console.error("Failed to fetch friends:", error);
      }
    };

    fetchFriends();
  }, [profile]);

  useEffect(() => {
    const fetchSports = async () => {
      if (!profile) return;

      try {
        const res = await api.get(
          `/api/user-profile-sports/profile/${profile.id}`
        );

        setSports(res.data.sports || []);
      } catch (error) {
        console.error("Failed to fetch profile sports:", error);
      }
    };

    fetchSports();
  }, [profile]);






  const storyItems = [
    {
      icon: BadgeCheck,
      type: "Official League Moment",
      title: "Stan scored 24 points in a league win",
      description:
        "Fast Action Basketball tagged Stan after a strong performance.",
      memoryDate: "Jul 20, 2026",
      postedDate: "Posted Jul 20, 2026",
      comments: 14,
      mediaType: "none",
      statLine: "24 PTS · 4 3PM · 5 REB",
      source: "Verified by Fast Action Basketball",
    },
    {
      icon: Trophy,
      type: "Achievement",
      title: "Won a weekend basketball championship",
      description:
        "A great run with the team and a close finish in the championship game.",
      memoryDate: "May 18, 2026",
      postedDate: "Posted May 19, 2026",
      comments: 10,
      mediaType: "image",
      image:
        "https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=500&q=80",
    },
    {
      icon: Camera,
      type: "Photo Memory",
      title: "Uploaded an old team photo",
      description:
        "Found an old team photo from one of our favorite seasons.",
      memoryDate: "Spring 2024",
      postedDate: "Posted Apr 5, 2026",
      comments: 7,
      mediaType: "image",
      image:
        "https://images.unsplash.com/photo-1526232761682-d26e03ac148e?auto=format&fit=crop&w=500&q=80",
    },
    {
      icon: Star,
      type: "Highlight",
      title: "Added a tennis highlight",
      description: "A long rally from a competitive match.",
      memoryDate: "Mar 22, 2026",
      postedDate: "Posted Mar 23, 2026",
      comments: 5,
      mediaType: "video",
      image:
        "https://images.unsplash.com/photo-1622279457486-62dcc4a431d6?auto=format&fit=crop&w=500&q=80",
    },
  ];

  const mediaItems = [
    {
      type: "video",
      title: "Summer league highlight",
      image:
        "https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=700&q=80",
    },
    {
      type: "image",
      title: "Old team photo",
      image:
        "https://images.unsplash.com/photo-1526232761682-d26e03ac148e?auto=format&fit=crop&w=700&q=80",
    },
    {
      type: "video",
      title: "Tennis rally clip",
      image:
        "https://images.unsplash.com/photo-1622279457486-62dcc4a431d6?auto=format&fit=crop&w=700&q=80",
    },
    {
      type: "image",
      title: "Tournament memory",
      image:
        "https://images.unsplash.com/photo-1519861531473-9200262188bf?auto=format&fit=crop&w=700&q=80",
    },
  ];

  const hasCurrentSeason = true;

  const getInitials = (name = "") => {
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  };




  const handleConnect = async () => {
    try {
      const res = await api.post("/api/friends/request", {
        receiver_profile_id: profile.id,
      });

      setFriendStatus("sent");
      setConnectionId(res.data.connection.id);
    } catch (error) {
      console.error("Failed to send friend request:", error);
    }
  };

  const handleAccept = async () => {
    try {
      await api.patch(`/api/friends/${connectionId}/accept`);

      setFriendStatus("accepted");

      const friendName =
        `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
        profile.display_name ||
        "this person";

      window.dispatchEvent(
        new CustomEvent("friend-accepted", {
          detail: { name: friendName },
        })
      );
    } catch (error) {
      console.error("Failed to accept friend request:", error);
    }
  };

  const handleDecline = async () => {
    try {
      await api.patch(`/api/friends/${connectionId}/decline`);
      setFriendStatus("none");
      setConnectionId(null);
    } catch (error) {
      console.error("Failed to decline friend request:", error);
    }
  };

  const handleCancelRequest = async () => {
    try {
      await api.delete(`/api/friends/${connectionId}/cancel`);
      setFriendStatus("none");
      setConnectionId(null);
    } catch (error) {
      console.error("Failed to cancel friend request:", error);
    }
  };

  const handleRemoveFriend = async () => {
    const confirmed = window.confirm("Remove this friend?");

    if (!confirmed) return;

    try {
      await api.delete(`/api/friends/${connectionId}`);

      setFriendStatus("none");
      setConnectionId(null);
      setShowFriendMenu(false);
    } catch (error) {
      console.error("Failed to remove friend:", error);
    }
  };



  const getSportIcon = (slug) => {
    const icons = {
      basketball: "🏀",
      tennis: "🎾",
      football: "🏈",
      baseball: "⚾",
      soccer: "⚽",
      volleyball: "🏐",
      golf: "⛳",
      bowling: "🎳",
      swimming: "🏊",
      running: "🏃",
      "jiu-jitsu": "🥋",
      taekwondo: "🥋",
      "water-polo": "🤽",
      "roller-derby": "🛼",
    };

    return icons[slug] || "⭐";
  };

  if (!profile) {
    return <div>Loading profile...</div>;
  }

  return (
    <div className="players-profile-page">
      <DirectoryNavbar />

      <main className="profile-layout">
        <section className="main-column">

          {/* Profile Hero */}
          <section className="profile-hero-card">
            <div className="profile-avatar-wrap">
              {profile.profile_image ? (
                <img
                  src={profile.profile_image}
                  alt={profile.display_name}
                  className="profile-avatar"
                />
              ) : (
                <div className="profile-avatar profile-avatar-fallback">
                  <UserRound size={42} />
                </div>
              )}
            </div>

            <div className="profile-hero-content">
              <p className="eyebrow-label">Sports Profile</p>

              <h1>
                {profile.first_name} {profile.last_name}
              </h1>

              {(profile.city || profile.state) && (
                <button
                  type="button"
                  className="profile-location-link"
                  onClick={() => {
                    const params = new URLSearchParams();

                    if (profile.state) {
                      params.set("state", profile.state);
                    }

                    if (profile.city) {
                      params.set("city", profile.city);
                    }

                    navigate(`/field?${params.toString()}`);
                  }}
                >
                  {[profile.city, profile.state]
                    .filter(Boolean)
                    .join(", ")}
                </button>
              )}

              {profile.bio && (
                <p className="profile-bio">
                  {profile.bio}
                </p>
              )}

              {sports.length > 0 && (
                <div className="profile-sports-section">
                  <span className="profile-sports-label">Sports</span>

                  <div className="sports-pill-row">
                    {sports.map((entry) => {
                      const sport = entry.sport;

                      if (!sport) return null;

                      const details = [
                        entry.skill_level,
                        entry.rating,
                        entry.position,
                      ].filter(Boolean);

                      return (
                        <div
                          className="sport-profile-item"
                          key={entry.id}
                        >
                          <div
                            className="sport-pill"
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              navigate(`/field?sport=${sport.slug}`)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                navigate(`/field?sport=${sport.slug}`);
                              }
                            }}
                          >
                            {/* <span className="sport-pill-icon">
                              {getSportIcon(sport.slug)}
                            </span> */}

                            <span className="sport-pill-name">
                              {sport.name}
                            </span>

                            {details.length > 0 && (
                              <span className="sport-pill-details">
                                · {details.join(" · ")}
                              </span>
                            )}

                            {entry.looking_to_play && (
                              <span
                                className="sport-open-dot"
                                title="Open to play"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {sports.some((entry) => entry.looking_to_play) && (
                    <div className="sport-status-legend">
                      <span className="sport-open-dot" />
                      <span>Open to play</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="profile-actions">
              {isOwnProfile ? (
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/profile/edit")}
                >
                  Edit Profile
                </button>
              ) : user ? (
                <>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/messages?profile=${profile.id}`)
                      }
                    >
                      {/* <MessageCircle size={16} /> */}
                      Message
                    </button>
                  {friendStatus === "none" && (
                    <button type="button" onClick={handleConnect}>
                      Connect
                    </button>
                  )}

                  {friendStatus === "sent" && (
                    <button type="button" onClick={handleCancelRequest}>
                      Cancel Request
                    </button>
                  )}

                  {friendStatus === "received" && (
                    <button
                      type="button"
                      onClick={() => setShowFriendRequestModal(true)}
                    >
                      Respond to Request
                    </button>
                  )}

                  {friendStatus === "accepted" && (
                    <div className="friend-menu-wrap">
                      <button
                        type="button"
                        onClick={() => setShowFriendMenu((prev) => !prev)}
                      >
                        Friend ▾
                      </button>

                      {showFriendMenu && (
                        <div className="friend-menu">
                          <button
                            type="button"
                            onClick={handleRemoveFriend}
                          >
                            Remove Friend
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </section>

          {/* Official Player Profiles */}
          {(profile.claimedPlayers || []).length > 0 && (
            <section className="profile-official-sports-card">
              <div className="simple-section-header">
                <div>
                  <p className="eyebrow-label">
                    Official Sports
                  </p>

                  <h2>My Player Profiles</h2>
                </div>
              </div>

              <div className="profile-official-sports-list">
                {(profile.claimedPlayers || []).map((player) => (
                  <article
                    key={player.playerId}
                    className="profile-official-player"
                  >
                    <div>
                      <span className="profile-official-sport">
                        {player.sportName}
                      </span>

                      <h3>{player.officialName}</h3>

                      <p>{player.organizationName}</p>

                      <small>
                        {player.teamName} • {player.leagueName} •{" "}
                        {player.seasonName}
                      </small>
                    </div>

                    {player.status === "verified" && (
                      <span className="profile-official-verified">
                        ✓ Verified
                      </span>
                    )}
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* Starting Lineup */}
          <section className="lineup-card">
            <div className="lineup-header">
              <div>
                <h2>My Starting Lineup</h2>
                <p>Only 5 spots. The people I’d always want on my team.</p>
              </div>

              <span className="lineup-count">
                {startingLineup.length}/5
              </span>
            </div>
            <div className="lineup-grid">
              {startingLineup.map((entry) => {
                const person = entry.friendProfile;

                if (!person) return null;

                const personName =
                  `${person.first_name || ""} ${person.last_name || ""}`.trim() ||
                  person.display_name;

                return (
                  <article
                    className="lineup-person-card"
                    key={entry.id}
                    onClick={() => navigate(`/profile/${person.id}`)}
                  >
                    {entry.sport && (
                      <div className="lineup-sport-badge">
                        <span>{getSportIcon(entry.sport.slug)}</span>
                      </div>
                    )}
                    {person.profile_image ? (
                      <img
                        src={person.profile_image}
                        alt={personName}
                        className="lineup-avatar"
                      />
                    ) : (
                      <div className="lineup-avatar lineup-avatar-fallback">
                        <UserRound size={34} />
                      </div>
                    )}

                    <h3>{personName}</h3>

                    {entry.quote && (
                      <p>“{entry.quote}”</p>
                    )}
                  </article>
                );
              })}
            </div>
          </section>


          {/* <section className="profile-tabs-card">
            <div className="profile-tabs">
              <button
                type="button"
                className={activeTab === "timeline" ? "active" : ""}
                onClick={() => setActiveTab("timeline")}
              >
                Timeline
              </button>

              <button
                type="button"
                className={activeTab === "media" ? "active" : ""}
                onClick={() => setActiveTab("media")}
              >
                Media
              </button>


            </div>

    
            {activeTab === "timeline" && (
              <section className="story-card">
                <div className="section-header">
                  <div>
                    <p className="eyebrow-label">
                      Timeline
                    </p>

                    <h2>Sports Timeline</h2>

                    <p className="section-subtitle">
                      Moments from {profile.displayName}’s sports life,
                      sorted by when they happened.
                    </p>
                  </div>

                  <div className="story-controls">
                    <button type="button">
                      All Moments ⌄
                    </button>

                    <button type="button">
                      ☰ Filter
                    </button>
                  </div>
                </div>

                <div className="timeline">
                  {storyItems.map((item, index) => (
                    <article
                      className="timeline-item"
                      key={index}
                    >
                      <div className="timeline-line">
                        <span className="timeline-dot" />
                      </div>

                      <div
                        className={`story-item-content ${item.type === "Official League Moment"
                          ? "official-moment"
                          : ""
                          }`}
                      >
                        <div className="story-icon">
                          <item.icon
                            size={24}
                            strokeWidth={2.2}
                          />
                        </div>

                        <div className="story-text">
                          <div className="story-type-row">
                            <span>{item.type}</span>

                            {item.source && (
                              <small>{item.source}</small>
                            )}
                          </div>

                          <h3>{item.title}</h3>

                          <p>{item.description}</p>

                          {item.statLine && (
                            <div className="stat-line-pill">
                              {item.statLine}
                            </div>
                          )}

                          <span className="posted-date">
                            {item.postedDate}
                          </span>
                        </div>

                        <span className="story-date">
                          {item.memoryDate}
                        </span>

                        {item.mediaType !== "none" ? (
                          <div className="story-media">
                            <img
                              src={item.image}
                              alt={item.title}
                              className="story-thumb"
                            />

                            {item.mediaType === "video" && (
                              <span className="story-play-badge">
                                <Play
                                  size={13}
                                  fill="currentColor"
                                />
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="story-no-media">
                            <item.icon
                              size={20}
                              strokeWidth={2.2}
                            />
                          </div>
                        )}

                        <div className="comment-count">
                          <MessageCircle size={16} />
                          <span>{item.comments}</span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                <div className="view-more-wrap">
                  <button
                    type="button"
                    className="view-more-btn"
                  >
                    View More Moments ↓
                  </button>
                </div>
              </section>
            )}


            {activeTab === "media" && (
              <section className="media-card">
                <div className="section-header">
                  <div>
                    <p className="eyebrow-label">
                      Photos & Videos
                    </p>

                    <h2>Media</h2>

                    <p className="section-subtitle">
                      Photos, highlights, and videos from{" "}
                      {profile.displayName}’s sports life.
                    </p>
                  </div>
                </div>

                <div className="media-grid">
                  {mediaItems.map((item, index) => (
                    <article
                      className="media-item"
                      key={index}
                    >
                      <img
                        src={item.image}
                        alt={item.title}
                      />

                      <div className="media-overlay">
                        {item.type === "video" ? (
                          <span className="media-type-badge">
                            <Play
                              size={14}
                              fill="currentColor"
                            />
                            Video
                          </span>
                        ) : (
                          <span className="media-type-badge">
                            <Image size={14} />
                            Photo
                          </span>
                        )}
                      </div>

                      <h3>{item.title}</h3>
                    </article>
                  ))}
                </div>
              </section>
            )} 
          </section> */}
        </section>

        {/* Right Sidebar */}
        <aside className="side-column">

          <section className="side-card sports-circle-card">
            <div className="side-card-header">
              <h2>Sports Circle</h2>

              <button
                type="button"
                className="sports-circle-view-all"
                onClick={() => navigate(`/profile/${profile.id}/friends`)}
              >
                View all
              </button>
            </div>

            <div className="sports-circle-list">
              {friends.slice(0, 4).map((friend) => {
                const person = friend.profile;

                return (
                  <div
                    key={person.id}
                    className="circle-person"
                    onClick={() => navigate(`/profile/${person.id}`)}
                  >
                    {person.profile_image ? (
                      <img
                        className="sports-circle-avatar"
                        src={person.profile_image}
                        alt={`${person.first_name || ""} ${person.last_name || ""}`}
                      />
                    ) : (
                      <div className="circle-person-fallback">
                        <UserRound size={18} />
                      </div>
                    )}

                    <span>
                      {`${person.first_name || ""} ${person.last_name || ""}`.trim() ||
                        person.display_name}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>

          {/* {hasCurrentSeason && (
            <section className="side-card season-card">
              <h2>Current Season</h2>

              <div className="season-league">
                <span>
                  Fast Action Basketball
                </span>

                <strong>
                  Panthers · Spring 2026
                </strong>
              </div>

              <div className="season-stat-grid">
                <div>
                  <strong>12</strong>
                  <span>Games</span>
                </div>

                <div>
                  <strong>18.6</strong>
                  <span>PPG</span>
                </div>

                <div>
                  <strong>6.4</strong>
                  <span>RPG</span>
                </div>

                <div>
                  <strong>2.1</strong>
                  <span>APG</span>
                </div>
              </div>

              <a
                href="/stats"
                className="side-link"
              >
                View season stats →
              </a>
            </section>
          )} */}


        </aside>
      </main>
      {showFriendRequestModal && (
        <FriendRequestModal
          requester={profile}
          onAccept={async () => {
            await handleAccept();
            setShowFriendRequestModal(false);
          }}
          onDecline={async () => {
            await handleDecline();
            setShowFriendRequestModal(false);
          }}
          onViewProfile={() => setShowFriendRequestModal(false)}
          onClose={() => setShowFriendRequestModal(false)}
        />
      )}
    </div>
  );
};

export default SportsProfilePage;